/**
 * Place Overlay Image Grid Component
 * Displays a grid of images with a "View all images" button
 * Follows Single Responsibility Principle
 */

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import {
  backgroundColors,
  spacing,
  borders,
  dimensions,
} from '@/design-system/utils';
import { useImageModalStore } from '@/store/imageModalStore';
import ImageModal from '@/components/common/modal/ImageModal';
import OverlayButton from '@/components/common/OverlayButton';

export interface PlaceOverlayImageGridProps {
  images: string[];
  placeName: string;
  onViewAll?: () => void;
  className?: string;
}

export const PlaceOverlayImageGrid: React.FC<PlaceOverlayImageGridProps> = ({
  images,
  placeName,
  onViewAll,
  className,
}) => {
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const { imageModalOpen, initialImage, openImageModal, closeImageModal } =
    useImageModalStore();

  const imagesLength = images.length;
  const firstImageUrl = images[0];
  // Reset expanded state when place/images change (parent keys by location so we usually remount; this handles same-length image sets)
  useEffect(() => {
    setShowAllPhotos(false);
  }, [placeName, imagesLength, firstImageUrl]);

  if (!images || images.length === 0) {
    return null;
  }

  const toggleShowAllPhotos = () => {
    setShowAllPhotos(prev => !prev);
  };

  const initialCount = 3; // Initial layout: 1 large left + 2 stacked right
  const imagesToShow = showAllPhotos ? images : images.slice(0, initialCount);
  const isInitialLayout = !showAllPhotos && images.length >= 2;
  const useInitialLayout =
    isInitialLayout &&
    (images.length === 2 ||
      (images.length >= 3 && imagesToShow.length === initialCount));

  return (
    <div className={className}>
      {useInitialLayout ? (
        <div
          style={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gridTemplateRows: '1fr 1fr',
            gap: spacing[2],
            height: dimensions.component.placeOverlayImage.large,
          }}
        >
          {/* Left: first image spans both rows */}
          <div
            style={{
              position: 'relative',
              gridColumn: 1,
              gridRow: '1 / -1',
              overflow: 'hidden',
              cursor: 'pointer',
            }}
            onClick={() => openImageModal(0)}
          >
            <Image
              src={images[0] || '/placeholder.jpg'}
              alt={`${placeName} 1`}
              fill
              style={{ objectFit: 'cover' }}
            />
          </div>
          {/* Right top: second image (if present) */}
          {images.length >= 2 && (
            <div
              style={{
                position: 'relative',
                gridColumn: 2,
                gridRow: 1,
                overflow: 'hidden',
                cursor: 'pointer',
              }}
              onClick={() => openImageModal(1)}
            >
              <Image
                src={images[1] || '/placeholder.jpg'}
                alt={`${placeName} 2`}
                fill
                style={{ objectFit: 'cover' }}
              />
            </div>
          )}
          {/* Right bottom: third image; "Show All Photos" here when more than 3 */}
          {images.length >= 3 && (
            <div
              style={{
                position: 'relative',
                gridColumn: 2,
                gridRow: 2,
                overflow: 'hidden',
                cursor: 'pointer',
              }}
              onClick={() => openImageModal(2)}
            >
              <Image
                src={images[2] || '/placeholder.jpg'}
                alt={`${placeName} 3`}
                fill
                style={{ objectFit: 'cover' }}
              />
              {images.length > initialCount && (
                <OverlayButton
                  label='Show All Photos'
                  onClick={e => {
                    e.stopPropagation();
                    toggleShowAllPhotos();
                    onViewAll?.();
                  }}
                />
              )}
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns:
              showAllPhotos || imagesToShow.length > 1
                ? 'repeat(2, 1fr)'
                : '1fr',
            gridAutoRows: 'auto',
            gap: spacing[2],
          }}
        >
          {imagesToShow.map((imageUrl, localIndex) => {
            const actualIndex = localIndex;
            const isLastOddItem =
              showAllPhotos &&
              imagesToShow.length % 2 === 1 &&
              localIndex === imagesToShow.length - 1;

            return (
              <div
                key={`image-${imageUrl}-${actualIndex}`}
                onClick={() => openImageModal(actualIndex)}
                style={{
                  position: 'relative',
                  width: '100%',
                  height: showAllPhotos
                    ? dimensions.component.placeOverlayImage.medium
                    : dimensions.component.placeOverlayImage.large,
                  borderRadius: 0,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  ...(isLastOddItem && { gridColumn: 'span 2' }),
                }}
              >
                <Image
                  src={imageUrl || '/placeholder.jpg'}
                  alt={`${placeName} ${actualIndex + 1}`}
                  fill
                  style={{ objectFit: 'cover' }}
                />
                {!showAllPhotos &&
                  !useInitialLayout &&
                  localIndex === imagesToShow.length - 1 &&
                  images.length > imagesToShow.length && (
                    <OverlayButton
                      label='Show All Photos'
                      onClick={e => {
                        e.stopPropagation();
                        toggleShowAllPhotos();
                        onViewAll?.();
                      }}
                    />
                  )}
              </div>
            );
          })}
        </div>
      )}

      {/* Toggle button when expanded - show so user can return to initial 1+2 layout */}
      {showAllPhotos && images.length > initialCount && (
        <div
          style={{
            position: 'relative',
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: spacing[2],
            paddingRight: spacing[2],
          }}
        >
          <button
            onClick={toggleShowAllPhotos}
            style={{
              backgroundColor: backgroundColors.secondary,
              border: `${borders.width.thin} solid ${backgroundColors.shade}`,
              borderRadius: borders.radius.full,
              padding: spacing[1.5],
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}
            title='Show less photos'
          >
            <Image
              src='/assets/images/svg/doublearrow.svg'
              alt='Show less'
              width={24}
              height={24}
            />
          </button>
        </div>
      )}

      {/* Image Modal */}
      {imageModalOpen && (
        <ImageModal
          images={images}
          initialSlide={initialImage}
          onClose={closeImageModal}
        />
      )}
    </div>
  );
};
